/*!
 * JapHunter Cart — Centralized cart engine
 * --------------------------------------------------
 * One module, one source of truth, called from drawer + cart page + PDP.
 *
 * Responsibilities:
 *  - Smart add: route /cart/add.js → /cart/change.js when variant already exists (no duplicate lines)
 *  - Stock guard: detect 422 errors AND silent clamps; show English toast
 *  - Free Random Booster: auto add at CHF 100+, auto remove below
 *  - One operation queue so concurrent clicks can't race
 *  - Single state + listeners; emits `jh:cart:updated` after every mutation
 *
 * Public API (window.JapHunter.cart):
 *    .addItem(variantId, qty, properties?)     → returns Promise<cart>
 *    .updateLine(key, qty)                     → returns Promise<cart>
 *    .removeLine(key)                          → returns Promise<cart>
 *    .refresh()                                → re-fetch + gift sync + notify
 *    .onUpdate(fn)                             → register a listener (fires immediately if cart loaded)
 *    .get()                                    → current cached cart
 *    .toast(msg, isError)                      → show English toast
 *
 * Network interception (transparent):
 *    fetch('/cart/add.js', …) and XHR-based $.ajax('/cart/add.js') are intercepted and
 *    re-routed through addItem so legacy code paths get merge-on-add for free.
 *
 * Design / DOM is owned by snippets/jh-cart-drawer.liquid and sections/main-cart-japhunter.liquid.
 * This file never touches the design — only logic + cart-state + a single transient toast.
 */
(function(){
  'use strict';

  // ─── Config ───
  var GIFT_VARIANT_ID = 52767531565379;
  var GIFT_THRESHOLD  = 10000; // CHF 100 in cents

  // ─── Capture native fetch + XHR before any other intercepts ───
  var _origFetch    = window.fetch ? window.fetch.bind(window) : null;
  var _origXhrOpen  = XMLHttpRequest.prototype.open;
  var _origXhrSend  = XMLHttpRequest.prototype.send;
  if(!_origFetch){ console.warn('[JHCart] fetch API not available'); return; }

  // _jhCartInternal = our own internal calls; bypass intercepts to avoid recursion
  var _internal = false;

  // ─── Raw HTTP (always bypass intercepts) ───
  function rawGetCart(){
    return _origFetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function(r){ return r.json(); });
  }
  function rawAdd(body){
    _internal = true;
    return _origFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function(r){ _internal = false; return r; }, function(e){ _internal = false; throw e; });
  }
  function rawChange(body){
    _internal = true;
    return _origFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function(r){ _internal = false; return r; }, function(e){ _internal = false; throw e; });
  }

  // ─── Toast (English, fixed bottom of viewport, auto-dismiss) ───
  var _toastEl = null, _toastTimer = null;
  function ensureToastStyles(){
    if(document.getElementById('jh-cart-toast-styles')) return;
    var s = document.createElement('style');
    s.id = 'jh-cart-toast-styles';
    s.textContent = ''
      + '.jh-cart-toast{position:fixed;left:50%;bottom:24px;transform:translate(-50%,20px);max-width:92vw;padding:13px 20px;background:#0a0a0a;color:#fff;border-radius:10px;font-size:13.5px;font-weight:600;line-height:1.4;text-align:center;box-shadow:0 8px 28px rgba(0,0,0,.28);z-index:99999;opacity:0;pointer-events:none;transition:opacity .22s ease,transform .22s ease}'
      + '.jh-cart-toast.is-open{opacity:1;transform:translate(-50%,0);pointer-events:auto}'
      + '.jh-cart-toast.is-error{background:#b91c1c}'
      + '@media (prefers-reduced-motion:reduce){.jh-cart-toast{transition:none}}';
    document.head.appendChild(s);
  }
  function toast(message, isError){
    if(!message) return;
    ensureToastStyles();
    if(!_toastEl){
      _toastEl = document.createElement('div');
      _toastEl.className = 'jh-cart-toast';
      _toastEl.setAttribute('role', 'status');
      _toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(_toastEl);
    }
    _toastEl.textContent = message;
    _toastEl.classList.toggle('is-error', !!isError);
    requestAnimationFrame(function(){ _toastEl.classList.add('is-open'); });
    if(_toastTimer) clearTimeout(_toastTimer);
    _toastTimer = setTimeout(function(){ if(_toastEl) _toastEl.classList.remove('is-open'); }, 4000);
  }

  // ─── Header cart bubble (universal) ───
  // Updates every .cart-count-bubble in the DOM so the header icon count
  // stays in sync without relying on a page reload.
  function updateHeaderBubble(count){
    var bubbles = document.querySelectorAll('.cart-count-bubble');
    if(count > 0){
      if(bubbles.length === 0){
        // Inject into common header cart hosts if no bubble exists yet
        document.querySelectorAll('.cart-icon-bubble, a[href$="/cart"]').forEach(function(host){
          if(host.querySelector('.cart-count-bubble')) return;
          var b = document.createElement('div');
          b.className = 'cart-count-bubble';
          b.innerHTML = '<span aria-hidden="true">'+(count<100?count:'99+')+'</span>'
                      + '<span class="visually-hidden">'+count+'</span>';
          host.appendChild(b);
        });
      } else {
        bubbles.forEach(function(b){
          var sp = b.querySelector('span[aria-hidden="true"]') || b.firstElementChild;
          if(sp) sp.textContent = (count<100?count:'99+');
          b.style.display = '';
          b.hidden = false;
        });
      }
    } else {
      bubbles.forEach(function(b){ b.style.display = 'none'; });
    }
  }

  // ─── State + listeners ───
  var _cart = null;
  var _listeners = [];
  function notify(){
    if(_cart){ try { updateHeaderBubble(_cart.item_count); } catch(e){} }
    _listeners.forEach(function(fn){ try { fn(_cart); } catch(e){ console.warn('[JHCart] listener threw', e); } });
    try { document.dispatchEvent(new CustomEvent('jh:cart:updated', { detail: { cart: _cart } })); } catch(e){}
  }

  // ─── Op queue (serial) ───
  var _queue = Promise.resolve();
  function enqueue(fn){
    var next = _queue.then(fn, fn).catch(function(err){ console.warn('[JHCart] op failed', err); return null; });
    _queue = next;
    return next;
  }

  // ─── Visible-property signature (ignores Shopify-internal `_*` keys) ───
  function visibleProps(props){
    if(!props || typeof props !== 'object') return '';
    var keys = Object.keys(props).filter(function(k){ return k && k.charAt(0) !== '_'; }).sort();
    if(!keys.length) return '';
    return keys.map(function(k){ return k + '=' + String(props[k]); }).join('&');
  }

  // ─── Stock-clamp detection helper ───
  function reportClampIfAny(requestedQty, lineKey, cart){
    if(!cart || !cart.items || requestedQty <= 0) return false;
    var line = cart.items.find(function(it){ return it.key === lineKey; });
    if(line && line.quantity < requestedQty){
      toast('Sorry, only ' + line.quantity + ' items are available in stock.', true);
      return true;
    }
    return false;
  }

  function handleErrorResponse(r){
    return r.clone().json().then(function(err){
      var msg = (err && err.description) || (err && err.message) || 'Cart update failed';
      toast(msg, true);
    }).catch(function(){
      toast('Cart update failed', true);
    });
  }

  // ─── Free Gift sync (virtual line via cart attribute) ───
  // We do NOT add the Random Booster as a real product anymore. Instead:
  //   - When cart subtotal ≥ CHF 100, set cart attribute `free_booster=yes`
  //   - When cart subtotal drops below, unset the attribute
  //   - The drawer + cart page render a visual-only "Random Booster FREE" line when this attribute is set
  //   - At checkout, the attribute is preserved on the order → fulfillment team includes a booster
  // This avoids all the Shopify BXGY line-split issues and the variant-at-0 abuse risk.
  function rawUpdate(body){
    _internal = true;
    return _origFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function(r){ _internal = false; return r; }, function(e){ _internal = false; throw e; });
  }

  function syncFreeGift(cart){
    if(!cart || !cart.items) return Promise.resolve({ cart: cart, changed: false });

    // Compute paid total (any real items — gift variant should never be in cart now)
    var paidTotal = cart.items
      .filter(function(it){ return it.variant_id !== GIFT_VARIANT_ID; })
      .reduce(function(s, it){ return s + it.final_line_price; }, 0);

    var hasAttr = (cart.attributes && (cart.attributes.free_booster === 'yes' || cart.attributes.free_booster === true));
    var legacyGiftLines = cart.items.filter(function(it){ return it.variant_id === GIFT_VARIANT_ID; });

    var ops = Promise.resolve();
    var anyChange = false;

    // 1) Remove ANY legacy Random Booster line items (from BXGY era / earlier tests)
    if(legacyGiftLines.length > 0){
      anyChange = true;
      legacyGiftLines.forEach(function(g){
        ops = ops.then(function(){ return rawChange({ id: g.key, quantity: 0 }); });
      });
    }

    // 2) Sync the attribute based on paid total
    var shouldHaveAttr = paidTotal >= GIFT_THRESHOLD;
    if(shouldHaveAttr && !hasAttr){
      anyChange = true;
      ops = ops.then(function(){ return rawUpdate({ attributes: { free_booster: 'yes' } }); });
    } else if(!shouldHaveAttr && hasAttr){
      anyChange = true;
      // Setting to empty string removes the attribute in Shopify
      ops = ops.then(function(){ return rawUpdate({ attributes: { free_booster: '' } }); });
    }

    if(!anyChange) return Promise.resolve({ cart: cart, changed: false });

    return ops.then(rawGetCart).then(function(c){ return { cart: c, changed: true }; });
  }

  // ─── After every mutation: refetch cart, sync gift, notify listeners ───
  function postOp(){
    return rawGetCart()
      .then(syncFreeGift)
      .then(function(result){
        _cart = result.cart;
        notify();
        return _cart;
      });
  }

  // ─── Public ops ───

  function addItem(variantId, quantity, properties){
    quantity = parseInt(quantity, 10) || 1;
    variantId = parseInt(variantId, 10);
    if(!variantId) return Promise.reject(new Error('No variant id'));

    // Gift variant: only ONE gift line ever. Guard against double-adds from any caller.
    // Adding the gift never fires `jh:cart:item-added` (it's a side effect, not a customer action).
    if(variantId === GIFT_VARIANT_ID){
      return enqueue(function(){
        return rawGetCart().then(function(cart){
          var hasGift = cart.items.some(function(it){ return it.variant_id === GIFT_VARIANT_ID; });
          if(hasGift){ return postOp(); } // already present — do nothing
          return rawAdd({ id: variantId, quantity: 1 }).then(function(r){
            if(!r.ok){ return handleErrorResponse(r).then(postOp); }
            return postOp();
          });
        });
      });
    }

    return enqueue(function(){
      return rawGetCart().then(function(cart){
        var newVisible = visibleProps(properties);
        var existing = cart.items.find(function(it){
          if(parseInt(it.variant_id, 10) !== variantId) return false;
          if(it.variant_id === GIFT_VARIANT_ID) return false;
          return visibleProps(it.properties) === newVisible;
        });

        if(existing){
          var newQty = existing.quantity + quantity;
          return rawChange({ id: existing.key, quantity: newQty }).then(function(r){
            if(!r.ok){ return handleErrorResponse(r).then(postOp); }
            return r.clone().json().then(function(c){
              reportClampIfAny(newQty, existing.key, c);
              return postOp().then(function(cart){
                try { document.dispatchEvent(new CustomEvent('jh:cart:item-added', { detail: { variantId: variantId, cart: cart } })); } catch(e){}
                return cart;
              });
            }).catch(function(){ return postOp(); });
          });
        }

        // New variant — original add
        var body = { id: variantId, quantity: quantity };
        if(properties && Object.keys(properties).length) body.properties = properties;
        return rawAdd(body).then(function(r){
          if(!r.ok){ return handleErrorResponse(r).then(postOp); }
          return postOp().then(function(cart){
            try { document.dispatchEvent(new CustomEvent('jh:cart:item-added', { detail: { variantId: variantId, cart: cart } })); } catch(e){}
            return cart;
          });
        });
      });
    });
  }

  function updateLine(key, quantity){
    quantity = parseInt(quantity, 10);
    if(isNaN(quantity)) quantity = 0;
    return enqueue(function(){
      return rawChange({ id: key, quantity: quantity }).then(function(r){
        if(!r.ok){ return handleErrorResponse(r).then(postOp); }
        return r.clone().json().then(function(c){
          reportClampIfAny(quantity, key, c);
          return postOp();
        }).catch(function(){ return postOp(); });
      });
    });
  }

  function removeLine(key){
    return updateLine(key, 0);
  }

  // ─── Body parsers for network interception ───
  function parseAddInit(init){
    var out = { variantId: null, quantity: 1, properties: null, multi: false };
    var body = init && init.body;
    if(!body) return out;
    try {
      if(typeof FormData !== 'undefined' && body instanceof FormData){
        out.variantId = parseInt(body.get('id'), 10);
        out.quantity = parseInt(body.get('quantity') || '1', 10) || 1;
        var props = {};
        body.forEach(function(value, key){
          var m = key.match(/^properties\[(.+)\]$/);
          if(m) props[m[1]] = String(value);
        });
        if(Object.keys(props).length) out.properties = props;
      } else if(typeof body === 'string'){
        try {
          var parsed = JSON.parse(body);
          if(Array.isArray(parsed.items)){ out.multi = true; return out; }
          out.variantId = parseInt(parsed.id, 10);
          out.quantity = parseInt(parsed.quantity || 1, 10) || 1;
          if(parsed.properties && typeof parsed.properties === 'object') out.properties = parsed.properties;
        } catch(e){
          var params = new URLSearchParams(body);
          out.variantId = parseInt(params.get('id'), 10);
          out.quantity = parseInt(params.get('quantity') || '1', 10) || 1;
        }
      }
    } catch(e){}
    return out;
  }

  function parseXhrBody(body){
    var out = { variantId: null, quantity: 1, properties: null };
    if(!body) return out;
    try {
      if(typeof FormData !== 'undefined' && body instanceof FormData){
        out.variantId = parseInt(body.get('id'), 10);
        out.quantity = parseInt(body.get('quantity') || '1', 10) || 1;
      } else if(typeof body === 'string'){
        try {
          var jp = JSON.parse(body);
          out.variantId = parseInt(jp.id, 10);
          out.quantity = parseInt(jp.quantity || 1, 10) || 1;
          if(jp.properties && typeof jp.properties === 'object') out.properties = jp.properties;
        } catch(e){
          var ps = new URLSearchParams(body);
          out.variantId = parseInt(ps.get('id'), 10);
          out.quantity = parseInt(ps.get('quantity') || '1', 10) || 1;
        }
      }
    } catch(e){}
    return out;
  }

  // ─── fetch() interception ───
  window.fetch = function(input, init){
    var url = '';
    try { url = (typeof input === 'string') ? input : (input && input.url) || ''; } catch(e){}
    var isAdd    = /\/cart\/add(\.js)?(\?|$)/.test(url);
    var isChange = /\/cart\/(change|update)(\.js)?(\?|$)/.test(url);

    if(isAdd && !_internal){
      var parsed = parseAddInit(init);
      if(parsed.variantId && !parsed.multi){
        // Route through smart-add and return a synthesized Response so callers using .json() work.
        return addItem(parsed.variantId, parsed.quantity, parsed.properties)
          .then(function(cart){
            var line = (cart && cart.items) ? cart.items.find(function(it){ return parseInt(it.variant_id, 10) === parsed.variantId; }) : null;
            var payload = line || cart || { items: [] };
            return new Response(JSON.stringify(payload), { status: 200, headers: { 'Content-Type': 'application/json' } });
          })
          .catch(function(err){
            var body = JSON.stringify({ status: 422, message: 'Cart Error', description: (err && err.message) || 'Add failed' });
            return new Response(body, { status: 422, headers: { 'Content-Type': 'application/json' } });
          });
      }
    }

    // For non-intercepted cart mutations, observe the response and run our post-op
    // (gift sync + bubble update + listener notification) once it succeeds.
    var p = _origFetch.apply(window, arguments);
    if(isChange && !_internal){
      p.then(function(res){
        if(res && res.ok){
          // Defer slightly so the caller's own .then handlers finish first
          setTimeout(function(){ postOp().catch(function(){}); }, 0);
        }
      }).catch(function(){});
    }
    return p;
  };

  // ─── XHR interception (for jQuery $.ajax and other XHR-based callers) ───
  XMLHttpRequest.prototype.open = function(method, url){
    try {
      this._jhMethod = (method || '').toUpperCase();
      this._jhUrl = url || '';
    } catch(e){}
    return _origXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(body){
    var xhr = this;
    var isAdd = xhr._jhMethod === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(xhr._jhUrl || '');

    if(!isAdd || _internal){
      return _origXhrSend.apply(this, arguments);
    }

    var parsed = parseXhrBody(body);
    if(!parsed.variantId || parsed.variantId === GIFT_VARIANT_ID){
      return _origXhrSend.apply(this, arguments);
    }

    // Queue this XHR so the cart check happens AFTER any pending add/change has committed.
    // Without this, two concurrent clicks would both see a stale cart and both create new lines.
    var sendArgs = arguments;
    enqueue(function(){
      return new Promise(function(resolve){
        rawGetCart().then(function(cart){
          var newVisible = visibleProps(parsed.properties);
          var existing = cart.items.find(function(it){
            if(parseInt(it.variant_id, 10) !== parsed.variantId) return false;
            if(it.variant_id === GIFT_VARIANT_ID) return false;
            return visibleProps(it.properties) === newVisible;
          });

          // Cleanup hook fires on XHR completion (success or failure): refresh cart state + emit event
          var firedComplete = false;
          var onLoadEnd = function(){
            if(firedComplete) return;
            firedComplete = true;
            if(xhr.status >= 200 && xhr.status < 400){
              postOp().then(function(c){
                try { document.dispatchEvent(new CustomEvent('jh:cart:item-added', { detail: { variantId: parsed.variantId, cart: c } })); } catch(e){}
                resolve();
              }).catch(function(){ resolve(); });
            } else {
              resolve();
            }
          };
          xhr.addEventListener('loadend', onLoadEnd);

          if(existing){
            // Re-open this XHR to /cart/change.js with the merged quantity
            try {
              _internal = true;
              _origXhrOpen.call(xhr, 'POST', '/cart/change.js', true);
              try { xhr.setRequestHeader('Content-Type', 'application/json'); } catch(e){}
              try { xhr.setRequestHeader('Accept', 'application/json'); } catch(e){}
              var newQty = existing.quantity + parsed.quantity;
              var newBody = JSON.stringify({ id: existing.key, quantity: newQty });
              xhr.addEventListener('load', function(){
                _internal = false;
                if(xhr.status >= 200 && xhr.status < 400){
                  try { var resp = JSON.parse(xhr.responseText); reportClampIfAny(newQty, existing.key, resp); } catch(e){}
                }
              });
              xhr.addEventListener('error', function(){ _internal = false; });
              _origXhrSend.call(xhr, newBody);
            } catch(e){
              _internal = false;
              _origXhrSend.apply(xhr, sendArgs);
            }
          } else {
            _origXhrSend.apply(xhr, sendArgs);
          }
        }).catch(function(){
          // Network error fetching /cart.js — send original request so the user isn't stuck
          xhr.addEventListener('loadend', function(){ resolve(); });
          _origXhrSend.apply(xhr, sendArgs);
        });
      });
    });
  };

  // ─── Initial load ───
  // Run a full cart sync (refresh + gift threshold + dedupe) on every page load.
  // This catches stale state (multi-qty gift, orphan gift below threshold, missing gift above threshold)
  // and brings the DOM in line with the real cart state.
  function init(){
    rawGetCart()
      .then(function(cart){
        _cart = cart;
        notify();
        return syncFreeGift(cart);
      })
      .then(function(result){
        if(result && result.changed){
          _cart = result.cart;
          notify();
        }
      })
      .catch(function(){});
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Public API ───
  window.JapHunter = window.JapHunter || {};
  window.JapHunter.cart = {
    get: function(){ return _cart; },
    addItem: addItem,
    updateLine: updateLine,
    removeLine: removeLine,
    refresh: postOp,
    onUpdate: function(fn){
      if(typeof fn !== 'function') return;
      _listeners.push(fn);
      if(_cart) try { fn(_cart); } catch(e){}
    },
    toast: toast,
    GIFT_VARIANT_ID: GIFT_VARIANT_ID,
    GIFT_THRESHOLD: GIFT_THRESHOLD
  };
})();
